import { useState } from "react";
import { Star, Camera, MessageSquareQuote, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Stars = ({ rating, size = "w-4 h-4" }) => (
  <span className="flex gap-0.5" aria-label={`${rating} de 5 estrelas`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${size} ${i < rating ? "fill-yellow-400 text-yellow-400" : "text-slate-200"}`} />
    ))}
  </span>
);

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("pt-BR");
};

// Avaliações reais do produto: média, comentários aprovados e formulário (vai para moderação).
export const ProductReviews = ({ productId, reviews = [] }) => {
  const settings = useSettings();
  const [form, setForm] = useState({ name: "", city: "", rating: 5, text: "", photo: "", video_url: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [uploading, setUploading] = useState(false);

  if ((settings.features || {}).reviews === false) return null;

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1).replace(".", ",")
    : null;

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/reviews/upload", fd);
      setForm((f) => ({ ...f, photo: data.url }));
      toast.success("Foto anexada!");
    } catch (err) {
      toast.error(formatApiError(err, "Falha ao enviar a foto."));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return toast.error("Informe seu nome e sua avaliação.");
    setSending(true);
    try {
      await api.post("/reviews", { ...form, product_id: productId });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível enviar sua avaliação."));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-10" data-testid="product-reviews">
      <h2 className="font-heading text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
        <MessageSquareQuote className="w-5 h-5 text-brand" /> Avaliações de clientes
      </h2>

      {reviews.length > 0 ? (
        <div className="mt-3 flex items-center gap-3" data-testid="reviews-summary">
          <span className="font-heading text-3xl font-extrabold text-gray-900" data-testid="reviews-average">{avg}</span>
          <div>
            <Stars rating={Math.round(Number(avg.replace(",", ".")))} />
            <p className="text-xs text-gray-500 mt-0.5">{reviews.length} avaliação{reviews.length > 1 ? "ões" : ""} de clientes</p>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-500">Ainda não há avaliações aprovadas para este produto. Seja o primeiro a avaliar!</p>
      )}

      {reviews.length > 0 && (
        <ul className="mt-4 space-y-4" data-testid="reviews-list">
          {reviews.map((r) => (
            <li key={r.id} className="border border-gray-100 rounded-2xl p-4 bg-white" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3">
                {r.photo ? (
                  <img src={r.photo} alt="" className="w-10 h-10 rounded-full object-cover border border-gray-100" loading="lazy" decoding="async" />
                ) : (
                  <span className="w-10 h-10 rounded-full bg-brand/10 text-brand font-bold flex items-center justify-center text-sm">
                    {(r.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {r.name} {r.city && <span className="font-normal text-gray-400">· {r.city}</span>}
                  </p>
                  <div className="flex items-center gap-2">
                    <Stars rating={r.rating || 5} size="w-3.5 h-3.5" />
                    {r.created_at && <span className="text-[11px] text-gray-400">{fmtDate(r.created_at)}</span>}
                  </div>
                </div>
              </div>
              <p className="mt-2.5 text-sm text-gray-700 leading-relaxed">{r.text}</p>
              {r.video_url && (
                <a href={r.video_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-brand font-medium hover:underline">
                  Ver vídeo do cliente
                </a>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* formulário: avaliações entram pendentes e só aparecem após aprovação no painel */}
      <div className="mt-6 bg-slate-50 border border-slate-100 rounded-2xl p-5" data-testid="review-form">
        <h3 className="font-semibold text-gray-900 text-sm">Avalie este produto</h3>
        {sent ? (
          <p className="mt-2 text-sm text-green-700 bg-green-50 border border-green-100 rounded-xl p-3" data-testid="review-sent">
            Obrigado! Sua avaliação foi recebida e será publicada após análise da nossa equipe.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-3 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Seu nome *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-9" required data-testid="review-form-name" />
              </div>
              <div>
                <Label className="text-xs">Cidade</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-9" data-testid="review-form-city" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Sua nota *</Label>
              <div className="mt-1 flex gap-1" data-testid="review-form-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} aria-label={`${n} estrelas`} data-testid={`review-star-${n}`}>
                    <Star className={`w-6 h-6 ${n <= form.rating ? "fill-yellow-400 text-yellow-400" : "text-slate-300 hover:text-yellow-300"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Sua avaliação *</Label>
              <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} className="mt-1" required data-testid="review-form-text" placeholder="Conte como foi sua experiência com o produto e a entrega..." />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Foto (opcional)</Label>
                <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-500 cursor-pointer hover:border-brand hover:text-brand" data-testid="review-form-photo">
                  <Camera className="w-4 h-4" />
                  {uploading ? "Enviando..." : form.photo ? "Foto anexada" : "Anexar foto (até 2 MB)"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} />
                </label>
              </div>
              <div>
                <Label className="text-xs">Link de vídeo (opcional)</Label>
                <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtube.com/..." className="mt-1 h-9" data-testid="review-form-video" />
              </div>
            </div>
            <Button type="submit" disabled={sending || uploading} className="rounded-full" data-testid="review-form-submit">
              <Send className="w-4 h-4" /> {sending ? "Enviando..." : "Enviar avaliação"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};
